"use client";

import { Fragment } from "react";
import { ArrowUpRight, Phone, Mail } from "lucide-react";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";
import { CalInlineEmbed } from "@/components/calcom";

function parseCtaHeadline(text: string, lang: string) {
  if (lang === "fr") {
    const parts = text.split(/(diagnostic)/i);
    const afterDiagnostic = parts[2] || "";
    return (
      <>
        {parts[0]}
        <span className="italic text-gold">{parts[1]}</span>
        <span className="italic text-paper/40">{afterDiagnostic}</span>
      </>
    );
  }
  const parts = text.split(/(diagnostic)/i);
  const afterDiagnostic = parts[2] || "";
  return (
    <>
      {parts[0]}
      <span className="italic text-gold">{parts[1]}</span>
      <span className="italic text-paper/40">{afterDiagnostic}</span>
    </>
  );
}

function parseDirectTitle(text: string, lang: string) {
  const pattern = lang === "fr" ? /(architecte principal)/i : /(lead architect)/i;
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    pattern.test(part) ? (
      <span key={i} className="italic text-gold">{part}</span>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

export const FinalCTA = () => {
  const { t, lang } = useV2Translation();

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-navy text-paper py-24 lg:py-32"
    >
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        aria-hidden
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Row 1: Text + Contact */}
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-7">
            <h2
              data-testid="contact-headline"
              className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight"
            >
              {parseCtaHeadline(t.finalCta.headline, lang)}
            </h2>

            <p className="font-body text-lg lg:text-xl text-paper/80 mt-10 max-w-[58ch] leading-relaxed">
              {t.finalCta.subheadline}
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <a
                href="mailto:dev@leman-it.com?subject=Diagnostic%20Call%20Request"
                data-testid="contact-cta-primary"
                className="btn-primary inline-flex items-center justify-between gap-6 px-7 py-5 font-mono text-xs uppercase tracking-[0.18em] min-w-[280px]"
              >
                <span>{t.finalCta.cta}</span>
                <ArrowUpRight size={18} strokeWidth={2.2} />
              </a>
              <p className="font-mono text-xs text-paper/60 max-w-[28ch] leading-relaxed">
                {t.finalCta.microcopy}
              </p>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 lg:pl-10">
            <div className="border border-paper/20 p-8 lg:p-10 h-full flex flex-col justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-paper/60">
                  {t.finalCta.directLine}
                </p>
                <p className="font-display text-2xl lg:text-3xl mt-5 leading-snug">
                  {parseDirectTitle(t.finalCta.directTitle, lang)}
                </p>
              </div>

              <ul className="mt-10 space-y-5">
                <li
                  className="flex items-start gap-4"
                  data-testid="contact-email"
                >
                  <Mail size={16} className="text-gold mt-1 shrink-0" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-paper/50">
                      {t.finalCta.emailLabel}
                    </p>
                    <a
                      href={`mailto:${t.finalCta.email}`}
                      className="font-body text-base hover:text-gold transition-colors"
                    >
                      {t.finalCta.email}
                    </a>
                  </div>
                </li>
                <li
                  className="flex items-start gap-4"
                  data-testid="contact-phone"
                >
                  <Phone size={16} className="text-gold mt-1 shrink-0" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-paper/50">
                      {t.finalCta.phoneLabel}
                    </p>
                    <a
                      href={`tel:${t.finalCta.phone.replace(/\s/g, "")}`}
                      className="font-body text-base hover:text-gold transition-colors"
                    >
                      {t.finalCta.phone}
                    </a>
                  </div>
                </li>
              </ul>
              <div className="mt-auto pt-8 flex justify-end">
                <a
                  href="#top"
                  className="w-14 h-14 rounded-full border border-paper/20 flex items-center justify-center hover:border-gold hover:bg-gold/10 transition-colors"
                  aria-label="Back to top"
                >
                  <ArrowUpRight size={20} className="text-gold" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Calendar full width */}
        <div id="booking" className="mt-16">
          <CalInlineEmbed />
        </div>
      </div>
    </section>
  );
};
